import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Link as MuiLink,
  Modal,
  Box,
  IconButton,
} from "@mui/material";
import { Link as LinkIcon, Close as CloseIcon } from "@mui/icons-material";
import "./Calendario.css";

// Import event data
import eventosData from "../eventos.json";
import { fetchSection } from "../services/api";

interface EventoItem {
  id: number;
  titulo: {
    pt: string;
    en: string;
    es: string;
  };
  descricao: {
    pt: string;
    en: string;
    es: string;
  };
  data: string;
  url?: string;
}

const Calendario: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<EventoItem | null>(null);
  const [data, setData] = useState<EventoItem[]>(eventosData as EventoItem[]);

  useEffect(() => {
    // Pull live content from the worker; fall back to the bundled copy.
    let cancelled = false;
    fetchSection<EventoItem[]>("eventos", eventosData as EventoItem[]).then(
      (d) => {
        if (!cancelled) setData(d);
      }
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const getCurrentLang = (): "pt" | "en" | "es" => {
    const lang = i18n.language;
    if (lang === "en-US") return "en";
    if (lang === "es-ES") return "es";
    return "pt";
  };

  const currentLang = getCurrentLang();

  const filteredEvents = useMemo(() => {
    return data.filter((event) => {
      const title = event.titulo[currentLang].toLowerCase();
      const description = event.descricao[currentLang].toLowerCase();
      const searchLower = searchTerm.toLowerCase();

      const matchesSearch =
        searchTerm === "" ||
        title.includes(searchLower) ||
        description.includes(searchLower);

      let matchesDate = true;
      if (fromDate || toDate) {
        const eventDate = new Date(event.data.split("/").reverse().join("-"));

        if (fromDate) {
          const fromDateObj = new Date(fromDate);
          matchesDate = matchesDate && eventDate >= fromDateObj;
        }

        if (toDate) {
          const toDateObj = new Date(toDate);
          matchesDate = matchesDate && eventDate <= toDateObj;
        }
      }

      return matchesSearch && matchesDate;
    });
  }, [data, searchTerm, fromDate, toDate, currentLang]);

  // Sort events by date (most recent first)
  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      const dateA = new Date(a.data.split("/").reverse().join("-")).getTime();
      const dateB = new Date(b.data.split("/").reverse().join("-")).getTime();
      return dateB - dateA;
    });
  }, [filteredEvents]);

  const handleCardClick = (event: EventoItem) => {
    setSelectedEvent(event);
  };

  const handleCloseModal = () => {
    setSelectedEvent(null);
  };

  const formatDateForDisplay = (dateString: string): string => {
    const [day, month, year] = dateString.split("/");
    const monthNames = {
      pt: [
        "Jan",
        "Fev",
        "Mar",
        "Abr",
        "Mai",
        "Jun",
        "Jul",
        "Ago",
        "Set",
        "Out",
        "Nov",
        "Dez",
      ],
      en: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      es: [
        "Ene",
        "Feb",
        "Mar",
        "Abr",
        "May",
        "Jun",
        "Jul",
        "Ago",
        "Sep",
        "Oct",
        "Nov",
        "Dic",
      ],
    };

    const monthIndex = parseInt(month) - 1;
    const monthName = monthNames[currentLang][monthIndex];

    return `${monthName}, ${year}\n${day}`;
  };

  const formatDateForModal = (dateString: string): string => {
    const [day, month, year] = dateString.split("/");
    const monthNames = {
      pt: [
        "Janeiro",
        "Fevereiro",
        "Março",
        "Abril",
        "Maio",
        "Junho",
        "Julho",
        "Agosto",
        "Setembro",
        "Outubro",
        "Novembro",
        "Dezembro",
      ],
      en: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      es: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ],
    };

    const monthIndex = parseInt(month) - 1;
    const monthName = monthNames[currentLang][monthIndex];

    return `${day} ${monthName}, ${year}`;
  };

  return (
    <section id="meetings" className="calendario-section">
      <div className="calendario-container">
        {/* Header */}
        <div className="calendario-header">
          <h1 className="calendario-main-title">{t("eventos.title")}</h1>

          {/* Controls */}
          <div className="calendario-controls">
            <TextField
              type="text"
              placeholder={t("eventos.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              size="small"
              className="calendario-search-input"
            />

            <div className="calendario-date-filters">
              <TextField
                type="date"
                label={t("eventos.from")}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                className="calendario-date-input"
              />

              <TextField
                type="date"
                label={t("eventos.to")}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                className="calendario-date-input"
              />
            </div>
          </div>
        </div>

        {/* Events Cards */}
        <div className="calendario-cards-container">
          {sortedEvents.length === 0 ? (
            <div className="calendario-empty">
              <p>{t("eventos.noResults")}</p>
            </div>
          ) : (
            <div className="calendario-cards-scroll">
              {sortedEvents.map((event) => (
                <div
                  key={event.id}
                  className="calendario-card"
                  onClick={() => handleCardClick(event)}
                >
                  <div className="calendario-card-date">
                    <div className="calendario-card-month">
                      {formatDateForDisplay(event.data)
                        .split("\n")[0]
                        .toUpperCase()}
                    </div>
                    <div className="calendario-card-day">
                      {formatDateForDisplay(event.data).split("\n")[1]}
                    </div>
                  </div>
                  <div className="calendario-card-content">
                    <h3 className="calendario-card-title">
                      {event.titulo[currentLang]}
                    </h3>
                  </div>
                  {event.url && (
                    <div className="calendario-card-url">
                      <MuiLink
                        href={event.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="calendario-url-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <LinkIcon className="calendario-url-icon" />
                        <span>{event.url}</span>
                      </MuiLink>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Event Detail Modal */}
      <Modal
        open={selectedEvent !== null}
        onClose={handleCloseModal}
        className="calendario-modal"
      >
        <Box className="calendario-modal-content">
          <div className="calendario-modal-header">
            <h2 className="calendario-modal-title">
              {selectedEvent?.titulo[currentLang]}
            </h2>
            <IconButton
              onClick={handleCloseModal}
              className="calendario-modal-close"
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="calendario-modal-date">
            {selectedEvent && formatDateForModal(selectedEvent.data)}
          </div>

          <div className="calendario-modal-description">
            {selectedEvent?.descricao[currentLang]}
          </div>

          {selectedEvent?.url && (
            <div className="calendario-modal-url">
              <MuiLink
                href={selectedEvent.url}
                target="_blank"
                rel="noopener noreferrer"
                className="calendario-modal-url-link"
              >
                <LinkIcon className="calendario-modal-url-icon" />
                {selectedEvent.url}
              </MuiLink>
            </div>
          )}
        </Box>
      </Modal>
    </section>
  );
};

export default Calendario;
